import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import * as yup from 'yup';

import { cpfMask, createUrl, dateMask, phoneMask, request } from 'app/utils';
import formatDate, { convertDateToISO } from 'app/utils/formatDate';
import { Button, Flex, HelperText, Icons, Loading, Typography } from 'components';
import {
  BatchProducts,
  EducationalInput,
  EducationalInstitutionActivateCodeCustomer,
  EducationalInstitutionActivatedCodeResponse,
  useEducational,
} from 'features/educational';
import { useSchool } from 'features/school';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Alert, { useAlert } from 'features/alert';
import { AxiosError } from 'axios';

const ShieldIcon = styled(Icons)`
  width: 35px;
  color: ${({ theme }) => theme.colors.primary_400};
`;

const schema = yup.object({
  name: yup.string().required('Nome é obrigatório'),

  birthDate: yup.string().when(['requireBirthDate'], {
    is: (requireBirthDate: boolean) => {
      // console.log('requireBirthDate', requireBirthDate);
      return requireBirthDate ?? false;
    },
    then: yup
      .string()
      .required('Data de nascimento é obrigatória')
      .matches(/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/, 'Data de nascimento inválida'),
  }),

  contactEmail: yup
    .string()
    .email('E-mail inválido')
    .when(['requireContactEmail'], {
      is: (requireContactEmail: boolean) => requireContactEmail ?? false,
      then: yup.string().required('E-mail é obrigatório'),
    }),

  contactPhone: yup.string().when(['requireContactPhone', 'requireGuardianContactPhone'], {
    is: (requireContactPhone: boolean) => requireContactPhone ?? false,
    then: yup
      .string()
      .required('Telefone é obrigatório')
      .matches(/^\(\d{2}\) \d{4,5}-\d{4}$/, 'Telefone inválido'),
  }),

  taxId: yup.string().when(['requireTaxId'], {
    is: (requireTaxId: boolean) => requireTaxId ?? false,
    then: yup
      .string()
      .required('CPF é obrigatório')
      .matches(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, 'CPF inválido')
      .test('cpf-valid', 'CPF inválido', (value) => {
        if (!value) return false;
        const cpf = value.replace(/\D/g, '');
        let sum = 0;
        let remainder;

        if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;

        for (let i = 1; i <= 9; i++) sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
        remainder = (sum * 10) % 11;
        if (remainder === 10 || remainder === 11) remainder = 0;
        if (remainder !== parseInt(cpf.substring(9, 10))) return false;

        sum = 0;
        for (let i = 1; i <= 10; i++) sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
        remainder = (sum * 10) % 11;
        if (remainder === 10 || remainder === 11) remainder = 0;
        if (remainder !== parseInt(cpf.substring(10, 11))) return false;

        return true;
      }),
  }),

  grade: yup.string().when(['requireGrade'], {
    is: (requireGrade: boolean) => requireGrade ?? false,
    then: yup.string().required('Série é obrigatória'),
  }),

  class: yup.string().when(['requireClass'], {
    is: (requireClass: boolean) => requireClass ?? false,
    then: yup.string().required('Turma é obrigatória'),
  }),

  period: yup.string().when(['requirePeriod'], {
    is: (requirePeriod: boolean) => requirePeriod ?? false,
    then: yup.string().required('Período é obrigatório'),
  }),
});

const RegisterStudent = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const isLoggedUser = searchParams.get('logged');

  const { schoolUuid } = useSchool();
  const { activateCode, activationCode, setActivatedCode, studentDetail } = useEducational();
  const { setAlert, clearAlert } = useAlert();

  const { requireBirthDate, requireContactEmail, requireContactPhone, requireTaxId, requireGrade, requireClass, requirePeriod } = activateCode;

  const {
    handleSubmit,
    register,
    setValue,
    watch,

    formState: { errors, isSubmitting },
  } = useForm<EducationalInstitutionActivateCodeCustomer>({
    resolver: yupResolver(schema),
    context: {
      requireBirthDate,
      requireContactEmail,
      requireContactPhone,
      requireTaxId,
      requireGrade,
      requireClass,
      requirePeriod,
    },
  });

  const onSubmit = useCallback(
    async (formData: EducationalInstitutionActivateCodeCustomer) => {
      clearAlert();
      setLoading(true);

      const data = {
        schoolUuid,
        activationCode,

        name: formData.name,

        ...(activateCode.requireBirthDate && { birthDate: formatDate(convertDateToISO(`${formData.birthDate}`)) }),
        ...(activateCode.requireContactEmail && { contactEmail: formData.contactEmail }),
        ...(activateCode.requireContactPhone && { contactPhone: formData.contactPhone }),
        ...(activateCode.requireTaxId && { taxId: formData.taxId }),
        ...(activateCode.requireGrade && { grade: formData.grade }),
        ...(activateCode.requireClass && { class: formData.class }),
        ...(activateCode.requirePeriod && { period: formData.period }),
      };

      request<EducationalInstitutionActivatedCodeResponse>({
        url: '/api/v1/School/EducationalInstitution/ActivateCode',
        method: 'POST',
        data,
      })
        .then(({ data }) => {
          const { errorMessage } = data;

          if (errorMessage) {
            setAlert({ type: 'error', message: errorMessage });
            return;
          }

          setActivatedCode(data);
          navigate(createUrl('educational/register/success'));
        })
        .catch((error: AxiosError<{ errorMessage: string }>) => {
          let message = 'Ocorreu um erro ao tentar cadastrar o professor.';

          if (error.response?.data?.errorMessage) {
            message = error.response.data.errorMessage;
          }

          setAlert({ type: 'error', message });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [activateCode, schoolUuid, activationCode]
  );

  useEffect(() => {
    setValue('requireBirthDate', requireBirthDate);
    setValue('requireContactEmail', requireContactEmail);
    setValue('requireContactPhone', requireContactPhone);
    setValue('requireTaxId', requireTaxId);
    setValue('requireGrade', requireGrade);
    setValue('requireClass', requireClass);
    setValue('requirePeriod', requirePeriod);
  }, []);

  useEffect(() => {
    if (isLoggedUser && studentDetail) {
      setValue('name', studentDetail.name);
      setValue('birthDate', studentDetail.birthDate);
      setValue('contactEmail', studentDetail.contactEmail);
      setValue('contactPhone', studentDetail.contactPhone);
      setValue('taxId', studentDetail.taxId);
    }
  }, [isLoggedUser, studentDetail]);

  return (
    <>
      {loading && <Loading alpha />}

      <Typography type="type8" color="gray_600" fontWeight="bold" textAlign="center">
        Cadastro de professor
      </Typography>

      <Typography type="type3" color="gray_600" textAlign="center" mt={2}>
        Preencha os dados abaixo para criar seu perfil
      </Typography>

      <Flex
        flexGrow={0}
        borderWidth={1}
        borderStyle="solid"
        borderColor="#00BFBE99"
        borderRadius={4}
        bg="#00BFBE0D"
        p={{ m: 2, t: 3 }}
        mt={6}
        flexWrap="nowrap"
      >
        <ShieldIcon icon="shieldAlertOutline" />

        <Flex flexDirection="column" flexGrow={1} pl={{ m: 2, t: 3 }} style={{ gap: 2 }}>
          <Typography color="primary_400" fontWeight="bold">
            Atenção! Preencha corretamente.
          </Typography>

          <Typography type="type1" color="gray_600">
            Todos os dados serão enviados para a escola.
          </Typography>
        </Flex>
      </Flex>

      <BatchProducts />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex flexDirection="column" mt={4} mb={6}>
          <Flex flexGrow={0} flexWrap={{ m: 'wrap', t: 'nowrap' }} style={{ gap: 16 }}>
            <Flex width={{ m: 1 }} flexGrow={0} flexDirection="column" style={{ gap: 4 }}>
              <Typography color="gray_600" fontWeight="medium">
                Nome completo
              </Typography>
              <Flex flexDirection="column">
                <EducationalInput type="text" {...register('name')} />

                {errors?.name && <HelperText type="error">{errors?.name?.message}</HelperText>}
              </Flex>
            </Flex>

            {requireBirthDate && (
              <Flex width={{ m: 1, t: 200, d: 250 }} flexShrink={0} flexGrow={0} flexDirection="column" style={{ gap: 4 }}>
                <Typography color="gray_600" fontWeight="medium">
                  Data de nascimento
                </Typography>

                <Flex flexDirection="column">
                  <EducationalInput
                    type="text"
                    {...register('birthDate')}
                    placeholder="DD/MM/AAAA"
                    value={dateMask(`${watch('birthDate')}`)}
                    maxLength={10}
                  />
                  {errors?.birthDate && <HelperText type="error">{errors?.birthDate?.message}</HelperText>}
                </Flex>
              </Flex>
            )}
          </Flex>

          {(requireContactEmail || requireContactPhone) && (
            <Flex flexGrow={0} flexWrap={{ m: 'wrap', t: 'nowrap' }} mt={4} style={{ gap: 16 }}>
              {requireContactEmail && (
                <Flex width={{ m: 1, t: 1 }} flexGrow={0} flexDirection="column" style={{ gap: 4 }}>
                  <Typography color="gray_600" fontWeight="medium">
                    E-mail
                  </Typography>
                  <Flex flexDirection="column">
                    <EducationalInput type="text" {...register('contactEmail')} />
                    {errors?.contactEmail && <HelperText type="error">{errors?.contactEmail?.message}</HelperText>}
                  </Flex>
                </Flex>
              )}

              {requireContactPhone && (
                <Flex width={{ m: 1, t: requireContactEmail ? 300 : 1 }} flexShrink={0} flexGrow={0} flexDirection="column" style={{ gap: 4 }}>
                  <Typography color="gray_600" fontWeight="medium">
                    Telefone
                  </Typography>
                  <Flex flexDirection="column">
                    <EducationalInput
                      type="text"
                      {...register('contactPhone')}
                      placeholder="(00) 00000-0000"
                      value={phoneMask(`${watch('contactPhone')}`)}
                      maxLength={15}
                    />
                    {errors?.contactPhone && <HelperText type="error">{errors?.contactPhone?.message}</HelperText>}
                  </Flex>
                </Flex>
              )}
            </Flex>
          )}

          {requireTaxId && (
            <Flex flexGrow={0} flexWrap={{ m: 'wrap', t: 'nowrap' }} mt={4} style={{ gap: 16 }}>
              <Flex width={{ m: 1, t: 1 }} flexGrow={0} flexDirection="column" style={{ gap: 4 }}>
                <Typography color="gray_600" fontWeight="medium">
                  CPF
                </Typography>

                <Flex flexDirection="column">
                  <EducationalInput type="text" {...register('taxId')} value={cpfMask(`${watch('taxId')}`)} />
                  {errors?.taxId && <HelperText type="error">{errors?.taxId?.message}</HelperText>}
                </Flex>
              </Flex>
            </Flex>
          )}

          {(requireGrade || requireClass || requirePeriod) && (
            <Flex flexGrow={0} flexWrap={{ m: 'wrap', t: 'nowrap' }} mt={4} style={{ gap: 16 }}>
              {requireGrade && (
                <Flex width={{ m: 1, t: 1 }} flexGrow={0} flexDirection="column" style={{ gap: 4 }}>
                  <Typography color="gray_600" fontWeight="medium">
                    Série
                  </Typography>
                  <Flex flexDirection="column">
                    <EducationalInput type="text" {...register('grade')} />
                    {errors?.grade && <HelperText type="error">{errors?.grade?.message}</HelperText>}
                  </Flex>
                </Flex>
              )}

              {requireClass && (
                <Flex width={{ m: 1, t: 1 }} flexGrow={0} flexDirection="column" style={{ gap: 4 }}>
                  <Typography color="gray_600" fontWeight="medium">
                    Turma
                  </Typography>
                  <Flex flexDirection="column">
                    <EducationalInput type="text" {...register('class')} />
                    {errors?.class && <HelperText type="error">{errors?.class?.message}</HelperText>}
                  </Flex>
                </Flex>
              )}

              {requirePeriod && (
                <Flex width={{ m: 1, t: 1 }} flexGrow={0} flexDirection="column" style={{ gap: 4 }}>
                  <Typography color="gray_600" fontWeight="medium">
                    Período
                  </Typography>
                  <Flex flexDirection="column">
                    <EducationalInput type="text" {...register('period')} />
                    {errors?.period && <HelperText type="error">{errors?.period?.message}</HelperText>}
                  </Flex>
                </Flex>
              )}
            </Flex>
          )}
        </Flex>

        <Alert px={0} invert style={{ fontWeight: 'bold' }} />

        <Flex flexGrow={0}>
          <Button type="submit" disabled={isSubmitting}>
            Continuar
          </Button>
        </Flex>
      </form>
    </>
  );
};

export default RegisterStudent;
